import { parseParamsAllowListString } from '@pkg/config/misc/navigationQueryParamsAllowList';

type QueryValue = string | string[] | null | undefined;

const MAX_QUERY_VALUE_LEN = 512;
const MAX_HASH_LEN = 256;

function pickFirstString(value: unknown): string | null {
  if (value === undefined || value === null) {
    return null;
  }
  if (Array.isArray(value)) {
    const first = value.find((v) => v != null && v !== '');
    if (first == null || typeof first !== 'string') {
      return null;
    }
    return first;
  }
  if (typeof value === 'string') {
    return value;
  }
  return null;
}

/**
 * Returns null if the value is unsafe to carry in a URL query (XSS / injection heuristics).
 */
function sanitizeQueryStringValue(raw: string): string | null {
  let value = raw;
  if (value.length > MAX_QUERY_VALUE_LEN) {
    value = value.slice(0, MAX_QUERY_VALUE_LEN);
  }
  if (/[\0-\x08\x0B\x0C\x0E-\x1F\x7F]/.test(value)) {
    return null;
  }
  if (/[<>]/.test(value)) {
    return null;
  }
  if (/javascript:/i.test(value) || /vbscript:/i.test(value) || /data:text\/html/i.test(value)) {
    return null;
  }
  if (/\bon\w+\s*=/i.test(value)) {
    return null;
  }
  return value;
}

/**
 * Subset of `query` with only allow-listed keys, with values passed through {@link sanitizeQueryStringValue}.
 * Pass keys from Nuxt: `parseParamsAllowListString(useRuntimeConfig().public.paramsAllowList)` (see package docs).
 */
export function getSanitizedNavigationQuery(
  query: Record<string, QueryValue | QueryValue[] | undefined>,
  allowedKeys: readonly string[],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of allowedKeys) {
    const first = pickFirstString(query[key]);
    if (first == null) {
      continue;
    }
    const safe = sanitizeQueryStringValue(first);
    if (safe != null) {
      out[key] = safe;
    }
  }
  return out;
}

/**
 * Nuxt: use the comma-separated string from `useRuntimeConfig().public.paramsAllowList`
 * (maps to NUXT_PUBLIC_QUERY_PARAM_ALLOW_LIST) so the allow list is correct in the client bundle.
 */

export function getSanitizedNavigationQueryFromAllowListString(
  query: Record<string, QueryValue | QueryValue[] | undefined>,
  commaSeparatedAllowList: string | null | undefined,
): Record<string, string> {
  return getSanitizedNavigationQuery(query, parseParamsAllowListString(commaSeparatedAllowList));
}

/**
 * Preserved query (allow list + value sanitization) and safe fragment, e.g. when
 * re-applying the current URL to a new path (logo home, language switcher).
 */
export function getSanitizedNavigationQueryAndHash(
  query: Record<string, QueryValue | QueryValue[] | undefined>,
  hash: string | null | undefined,
  commaSeparatedAllowList: string | null | undefined,
): { query: Record<string, string>; hash: string | undefined } {
  return {
    query: getSanitizedNavigationQueryFromAllowListString(query, commaSeparatedAllowList),
    hash: getSanitizedNavigationHash(hash),
  };
}

/**
 * Fragment to pass through on navigation, or `undefined` if missing / unsafe.
 */
export function getSanitizedNavigationHash(
  hash: string | null | undefined,
): string | undefined {
  if (hash == null || hash === '' || hash === '#') {
    return undefined;
  }
  if (hash.length > MAX_HASH_LEN) {
    return undefined;
  }
  if (/[\0-\x1F\x7F<>\"'`\\]/.test(hash)) {
    return undefined;
  }
  if (/javascript:/i.test(hash) || /data:/i.test(hash) || /vbscript:/i.test(hash)) {
    return undefined;
  }
  return hash;
}
